<template>
  <div class="sidebar-menu pt-3" :class="{ 'collapsed': collapsed }">
    <div class="collapse-button">
      <Button class="menu-collapse-icon" @click="toggleCollapse" />
    </div>
    <PanelMenu v-model:expandedKeys="expandedKeys" :model="items" :router="true" />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, watch, ref } from 'vue';
import PanelMenu from 'primevue/panelmenu';
import { useRole } from '@/composables/useRole';
import { AppRoles } from '@/enums/ApplicationClaims.enum';
import Button from 'primevue/button';
import { useRouter, useRoute } from 'vue-router';

export default defineComponent({
  name: 'BoosterNav',
  components: {
    PanelMenu,
    Button,
  },
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:collapsed'],
  setup(props, { emit }) {
    const router = useRouter();
    const route = useRoute();
    const { hasRole } = useRole();
    const canViewInvestments = hasRole(AppRoles.InvestmentAdviser);
    const canViewInsurance = hasRole(AppRoles.InsuranceAdviser);
    const expandedKeys = ref<{ [key: string]: boolean }>({});

    const toggleCollapse = () => {
      emit('update:collapsed', !props.collapsed); 
    };

    const items = computed(() => {
      const currentPath = route.path; 
      const isActive = (paths: string | string[], exact = false) => {
        const routes = Array.isArray(paths) ? paths : [paths];

        return routes.some((p) => {
          if (p === "/") {
            return currentPath === "/";
          }
          if (exact) {
            return currentPath === p;
          }
          return currentPath === p || currentPath.startsWith(p + "/");
        })
          ? "active-menu-item"
          : "";
      };

      const productItems: any[] = [];
      if (canViewInvestments) {
        productItems.push({
          key: "investments",
          icon: props.collapsed ? 'investments-icon' : '',
          label: props.collapsed ? '' : 'Investments',
          to: '/products/investments',
          class: isActive('/products/investments', true),
          command: () => router.push('/products/investments'),
        });
      }
      if (canViewInsurance) {
        productItems.push({
          key: "insurance",
          icon: props.collapsed ? 'report-icon' : '',
          label: props.collapsed ? '' : 'Insurance',
          to: '/products/insurance',
          class: isActive('/products/insurance', true),
          command: () => router.push('/products/insurance'),
        });
      }

      const items: any[] = [
        {
          key: "home",
          icon: 'arrow-right-icon',
          label: props.collapsed ? '' : 'Adviser hub',
          to: '/',
          class: isActive('/'),
          command: () => router.push('/'),
        },
        {
          key: "clients",
          icon: 'client-icon',
          label: props.collapsed ? '' : 'Accounts and clients',
          to: '/accounts-and-clients',
          class: isActive([
            '/accounts-and-clients', 
            '/clients', 
          ]) || (currentPath.startsWith("/products/investments/") ? "active-menu-item" : ""),
          command: () => router.push('/accounts-and-clients'),
        },
        {
          key: "tools",
          icon: 'forms-icon',
          label: props.collapsed ? '' : 'Tools, forms and docs',
          to: '/tools-forms-docs',
          class: isActive('/tools-forms-docs'),
          command: () => router.push('/tools-forms-docs'),
        },
        // {
        //   icon: 'investments-icon',
        //   label: 'Investments'
        // },
        // {
        //   icon: 'report-icon',
        //   label: 'Create report'
        // },
        // {
        //   icon: 'group-icon',
        //   label: 'New account'
        // },
      ];

      if (productItems.length > 0) {
        items.push({
          key: "products",
          icon: 'product-icon-small',
          label: props.collapsed ? '' : 'Products',
          items: productItems,
        });
      }

      if(canViewInvestments) {
        items.push({
          key: "gcas",
          icon: 'gcas-icon',
          label: props.collapsed ? '' : 'BCAS',
          command: () => window.open(process.env.VUE_APP_GCAS_APP_URL, "_blank")
        });
      }

      return items;
    });

    watch(
      () => route.path,
      (newPath: any) => {
      if (newPath === "/products/investments" || newPath === "/products/insurance") {
        expandedKeys.value = { products: true };
      } else {
        expandedKeys.value = {};
      }
      },
      { immediate: true }
    );

    return {
      toggleCollapse,
      items,
      expandedKeys,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/scss/booster-styles.scss';

.sidebar-menu {
  padding-right: 0.625rem;
  .collapse-button {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    margin: 0.875rem 0 1.25rem 0;
  }
  :deep(.p-button-icon-only) {
    background-color: transparent;
    border: none;
    padding: 0rem !important;
    margin: 0rem -0.625rem 0rem 0rem !important;
  }
  :deep(.menu-collapse-icon) {
    display: inline-block;
    width: 1rem;
    height: 0.84rem;
    background-image: url('@/assets/menu-collapse-icon.svg');
    background-size: contain;
    background-repeat: no-repeat;
    border-width: 0px;
    background-color: transparent;
    &:hover {
      background-image: url('@/assets/menu-collapse-icon-blue.svg');
    }
  }
  :deep(.p-panelmenu-header-action) {
    position: relative;
  }
  :deep(.p-submenu-icon) {
    position: absolute;
    right: 0;
    height: 12px;
    transform: rotate(90deg);
  }
  :deep(.p-panelmenu-header-action[aria-expanded="true"] .p-submenu-icon),
  :deep(.p-panelmenu-header.p-highlight .p-submenu-icon) {
    transform: rotate(180deg);
  }
  :deep(.p-panelmenu-content) {
    background: transparent;
    padding: 0;
    border: none;
    .p-menuitem-content {
      &:hover {
        border-radius: 0.438rem;
      }
    }
  }
  :deep(.p-menuitem-link) {
    color: $booster-text;
    font-family: 'Nunito Sans';
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 400;
    line-height: 0.875rem;
    text-decoration: none;
    padding: 0.625rem;
    &:hover {
      border-radius: 0.438rem;
      background: $booster-consent-blue;
      color: $booster-text !important;
      .p-menuitem-text {
        color: $booster-text !important;
      }
    }
  }
  :deep(.p-panelmenu-panel) {
    .p-submenu-list {
      &.p-panelmenu-root-list {
        padding: 0 !important;
        .p-menuitem-link {
          padding-left: 32px;
          .investments-icon {
            width: 0.776rem;
            height: 1rem;
            background-image: url('@/assets/investment-icon-gray.svg');
          }
          .report-icon {
            width: 1rem;
            height: 0.84rem;
            background-image: url('@/assets/report-icon.svg');
          }
        }
      }
    }
    margin-bottom: 0.875rem;
    .p-panelmenu-header-content {
      background-color: transparent !important;
      border: none !important;
      .p-panelmenu-header-action {
        color: $booster-text;
        font-family: 'Nunito Sans';
        font-size: 0.875rem;
        font-style: normal;
        font-weight: 400;
        line-height: 0.875rem;
        text-decoration: none;
        padding: 0.625rem;
        &:hover {
          border-radius: 0.438rem;
          background: $booster-consent-blue;
          color: $booster-text;
        }
        .client-icon {
          display: inline-block;
          width: 1.1rem;
          height: 0.84rem;
          background-image: url('@/assets/account-and-client-icon.svg');
          background-size: contain;
          background-repeat: no-repeat;
        }
        .investments-icon {
          width: 0.776rem;
          height: 1rem;
          background-image: url('@/assets/investment-icon-gray.svg');
        }
        .report-icon {
          width: 1rem;
          height: 0.84rem;
          background-image: url('@/assets/report-icon.svg');
        }
        .group-icon {
          width: 1.188rem;
          height: 0.84rem;
          background-image: url('@/assets/group-icon.svg');
        }
        .forms-icon {
          width: 1rem;
          height: 1rem;
          background-image: url('@/assets/forms-icon.svg');
          background-repeat: no-repeat;
        }
        .gcas-icon {
          width: 0.84rem;
          height: 0.625rem;
          background-image: url('@/assets/gcas-icon.svg');
          background-repeat: no-repeat;
        }
        .arrow-right-icon {
          width: 1rem;
          height: 1rem;
          background-image: url('@/assets/home-gray.svg');
        }
        .product-icon-small {
          width: 0.868rem;
          height: 1rem;
          background-image: url('@/assets/product-icon-small.svg');
        }
      }
    }
    &.active-menu-item {
      .p-panelmenu-header-action {
        border-radius: 0.438rem;
        background: $booster-consent-blue;
        font-weight: 700;
        color: $booster-blue;
        &:hover {
          color: $booster-blue;
        }
        .arrow-right-icon {
          background-image: url('@/assets/home-icon-blue.svg');
        }
      }
    }
  }
  .p-menuitem-text {
    display: inline-block;
    opacity: 1;
    transform: translateX(0);
    visibility: visible;
    pointer-events: auto;
    transition: opacity 0.3s ease 0.1s, transform 0.3s ease 0.1s, visibility 0s;
  }
  &.collapsed {
    width: 60px;
    .collapse-button {
      justify-content: center;
    }
    :deep(.menu-collapse-icon) {
      background-position: center;
      background-image: url('@/assets/collapse-right-arrow.svg');
      &:hover {
         background-image: url('@/assets/collapse-right-arrow-blue.svg');
      }
    }
    
    .p-menuitem-text {
      pointer-events: none;
      opacity: 0;
      transform: translateX(-10px);
      transition: opacity 0.3s ease, transform 0.3s ease, visibility 0s linear 0.3s;
      visibility: hidden;
    }
     :deep(.p-panelmenu-panel) {
        .p-panelmenu-header-content {
          .p-panelmenu-header-action {
            padding: 0.75rem 0.5rem !important;
            justify-content: center;
            .p-menuitem-icon {
              margin-right: 0 !important;
            }
          }
        }
      }
    :deep(.p-submenu-icon) {

      display: none;
    }
    :deep(.p-panelmenu-panel) {
      .p-submenu-list {
        &.p-panelmenu-root-list {
          .p-menuitem-link {
            padding-left: 1rem;
          }
        }
      }
    }
  }
}
:deep(.p-panelmenu-panel) {
.p-submenu-list {
  &.p-panelmenu-root-list {
      margin: 0;
      .active-menu-item {
        .p-menuitem-link {
          border-radius: 0.438rem;
          background: #ecf1f4;
          font-weight: 700;
          color: #007abc !important;
          .p-menuitem-text {
            color: #007abc !important;
          }
        }
      }
      li {
        margin: 0.438rem 0;
      }
    }
  }
}
</style>
